// Service content aligned with Vietnam Immigration Department & government regulations.
// Icons reference lucide-react component names.

export const PASSPORT_FIELDS = [
  { name: "full_name", label: "Full Name (as in passport)", type: "text", required: true },
  { name: "passport_number", label: "Passport Number", type: "text", required: true },
  { name: "nationality", label: "Nationality", type: "text", required: true },
  { name: "date_of_birth", label: "Date of Birth", type: "date", placeholder: "DD/MM/YYYY", required: true },
  { name: "sex", label: "Sex", type: "select", options: ["M", "F"], required: true },
  { name: "place_of_birth", label: "Place of Birth", type: "text" },
  { name: "date_of_issue", label: "Passport Issue Date", type: "date", placeholder: "DD/MM/YYYY" },
  { name: "date_of_expiry", label: "Passport Expiry Date", type: "date", placeholder: "DD/MM/YYYY", required: true },
];

export const SERVICES = [
  {
    key: "visa_application",
    slug: "vietnam-visa-application",
    icon: "Plane",
    title: "Vietnam Visa Application",
    short: "E-visas, tourist, business & work-purpose entry visas for all eligible nationalities.",
    hero: "https://images.unsplash.com/photo-1581553673739-c4906b5d0de8?crop=entropy&cs=srgb&fm=jpg&q=85",
    summary:
      "We prepare and lodge your Vietnam entry visa application in full compliance with the Law on Entry, Exit, Transit and Residence of Foreigners in Vietnam (Law No. 47/2014/QH13, amended by Law No. 51/2019/QH14 and Law No. 23/2023/QH15). Since 15 August 2023 Vietnam issues 90-day e-visas with single or multiple entry to citizens of all countries and territories.",
    features: [
      "Single or multiple-entry e-visa (up to 90 days validity)",
      "Tourist (DL), business (DN1/DN2) and visitor (TT) categories",
      "Visa on Arrival approval letters for air entry",
      "Document review to prevent rejection and delays",
    ],
    requirements: [
      "Passport valid for at least 6 months with two blank pages",
      "Clear scan of the passport bio-data page",
      "Recent 4x6 cm passport-style photo, white background, no glasses",
      "Intended entry/exit dates and port of entry",
      "For business visas: sponsoring Vietnamese company documents",
    ],
    process: [
      { t: "Submit & auto-fill", d: "Upload your passport bio page — our system reads your details automatically." },
      { t: "Document review", d: "Our consultants verify eligibility and the correct visa category." },
      { t: "Lodgement", d: "We lodge the application with the Vietnam Immigration Department." },
      { t: "Approval & issue", d: "Receive your e-visa or approval letter by email, ready for entry." },
    ],
    processing: "3–5 working days (standard e-visa); 1–2 working days (urgent)",
    fee_note: "Government fee: USD 25 (single entry) / USD 50 (multiple entry) plus service fee. A precise quotation is issued after review.",
    regulations: [
      "Law No. 47/2014/QH13 on Entry, Exit, Transit and Residence of Foreigners",
      "Resolution No. 127/NQ-CP (2023) — 90-day e-visa policy",
      "Managed by the Vietnam Immigration Department, Ministry of Public Security",
    ],
    fields: [
      { name: "visa_type", label: "Visa Category", type: "select", required: true,
        options: ["Tourist (DL)", "Business (DN1/DN2)", "Visiting relatives (TT)", "E-visa", "Visa on Arrival"] },
      { name: "entries", label: "Number of Entries", type: "select", options: ["Single entry", "Multiple entry"], required: true },
      { name: "purpose", label: "Purpose of Visit", type: "text", required: true },
      { name: "entry_date", label: "Intended Entry Date", type: "date", placeholder: "DD/MM/YYYY", required: true },
      { name: "duration", label: "Requested Stay Duration", type: "select", options: ["30 days", "90 days"], required: true },
      { name: "entry_port", label: "Port of Entry", type: "select",
        options: ["Noi Bai (Hanoi)", "Tan Son Nhat (Ho Chi Minh City)", "Da Nang", "Cam Ranh", "Land border"] },
      { name: "address_vn", label: "Address / Hotel in Vietnam", type: "text" },
    ],
  },
  {
    key: "business_registration",
    slug: "vietnam-business-registration",
    icon: "Building2",
    title: "Vietnam Business Registration",
    short: "Company formation & Investment Registration Certificates for foreign investors.",
    hero: "https://images.unsplash.com/photo-1681569685386-b7bda397672e?crop=entropy&cs=srgb&fm=jpg&q=85",
    summary:
      "We establish your Vietnamese company end-to-end under the Law on Enterprises (No. 59/2020/QH14) and the Law on Investment (No. 61/2020/QH14). Foreign-invested entities require an Investment Registration Certificate (IRC) followed by an Enterprise Registration Certificate (ERC) issued by the provincial Department of Planning and Investment.",
    features: [
      "100% foreign-owned LLC, JSC or representative office setup",
      "Investment Registration Certificate (IRC) application",
      "Enterprise Registration Certificate (ERC) & tax code",
      "Registered address, seal, and post-licensing compliance",
    ],
    requirements: [
      "Passport of investor(s) / legal representative",
      "Proof of financial capacity (bank statement)",
      "Proposed company name, business lines and charter capital",
      "Registered office lease agreement in Vietnam",
      "Parent company documents (for corporate investors), legalised",
    ],
    process: [
      { t: "Consultation", d: "We confirm permitted business lines and ownership structure." },
      { t: "IRC application", d: "Investment Registration Certificate lodged with the DPI." },
      { t: "ERC & tax code", d: "Enterprise Registration Certificate and tax registration completed." },
      { t: "Go live", d: "Company seal, bank account guidance and post-licence compliance." },
    ],
    processing: "IRC: ~15 working days; ERC: ~3–5 working days after IRC",
    fee_note: "Fees depend on entity type, business lines and charter capital. A detailed quotation is issued after consultation.",
    regulations: [
      "Law on Enterprises No. 59/2020/QH14",
      "Law on Investment No. 61/2020/QH14",
      "Decree No. 01/2021/ND-CP on enterprise registration",
    ],
    fields: [
      { name: "company_name", label: "Proposed Company Name", type: "text", required: true },
      { name: "business_type", label: "Entity Type", type: "select", required: true,
        options: ["Limited Liability Company (LLC)", "Joint Stock Company (JSC)", "Representative Office", "Branch Office"] },
      { name: "business_lines", label: "Intended Business Lines", type: "textarea", required: true },
      { name: "charter_capital", label: "Charter / Investment Capital (USD)", type: "text", required: true },
      { name: "ownership", label: "Foreign Ownership (%)", type: "text", placeholder: "e.g. 100%" },
      { name: "address_vn", label: "Proposed Registered Address", type: "text" },
    ],
  },
  {
    key: "visa_extension",
    slug: "vietnam-visa-extension",
    icon: "CalendarClock",
    title: "Vietnam Visa Extension",
    short: "Extend or renew your current Vietnam visa without leaving the country.",
    hero: "https://images.unsplash.com/photo-1569098644584-210bcd375b59?crop=entropy&cs=srgb&fm=jpg&q=85",
    summary:
      "If you are already in Vietnam and wish to stay longer, we handle your visa extension or renewal with the Vietnam Immigration Department, in line with Circular No. 04/2015/TT-BCA and current fee schedules. Applications should be lodged before your current permitted stay expires to avoid an overstay penalty.",
    features: [
      "Tourist and business visa extensions",
      "In-country renewal — no need to exit Vietnam",
      "Temporary residence card (TRC) advisory for long stays",
      "Deadline tracking so you never overstay",
    ],
    requirements: [
      "Original passport with the current valid visa/entry stamp",
      "Valid current visa that has not yet expired",
      "For business extensions: sponsoring company documents",
      "Vietnamese address and completed extension form (NA5 where applicable)",
    ],
    process: [
      { t: "Eligibility check", d: "We confirm your current visa status and extension options." },
      { t: "Prepare documents", d: "Complete the required forms and sponsor letters." },
      { t: "Lodge with Immigration", d: "Submit to the Vietnam Immigration Department office." },
      { t: "Collect", d: "Receive your extended visa stamp / new validity." },
    ],
    processing: "5–7 working days (standard); urgent options available",
    fee_note: "Government fees vary by extension length and entry type. A quotation is issued after we review your current visa.",
    regulations: [
      "Circular No. 04/2015/TT-BCA on entry/exit forms",
      "Law No. 47/2014/QH13 (as amended 2019, 2023)",
      "Circular No. 25/2021/TT-BTC on immigration fees",
    ],
    fields: [
      { name: "current_visa_type", label: "Current Visa Type", type: "text", required: true },
      { name: "current_visa_expiry", label: "Current Visa Expiry Date", type: "date", placeholder: "DD/MM/YYYY", required: true },
      { name: "extension_length", label: "Requested Extension", type: "select", options: ["1 month", "3 months", "6 months"], required: true },
      { name: "entries", label: "Entry Type", type: "select", options: ["Single entry", "Multiple entry"] },
      { name: "address_vn", label: "Current Address in Vietnam", type: "text", required: true },
      { name: "notes", label: "Additional Notes", type: "textarea" },
    ],
  },
  {
    key: "overstay_report",
    slug: "overstay-report",
    icon: "AlertTriangle",
    title: "Overstay Report",
    short: "Resolve visa overstays and administrative penalties professionally and discreetly.",
    hero: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?crop=entropy&cs=srgb&fm=jpg&q=85",
    summary:
      "Overstaying a Vietnamese visa is an administrative violation under Decree No. 144/2021/ND-CP and can result in fines and future entry restrictions. We help you report the overstay, prepare the required explanation, settle the administrative penalty, and obtain an exit permit so you can leave or regularise your status lawfully.",
    features: [
      "Overstay declaration and explanation letter preparation",
      "Administrative penalty settlement guidance",
      "Exit permit / temporary stay resolution",
      "Advice to protect future entry eligibility",
    ],
    requirements: [
      "Passport with the expired visa/entry stamp",
      "Date your permitted stay expired",
      "Explanation of the reason for the overstay",
      "Vietnamese contact address",
    ],
    process: [
      { t: "Assessment", d: "We assess the length of overstay and applicable penalties." },
      { t: "Prepare report", d: "Draft the overstay declaration and explanation letter." },
      { t: "Immigration liaison", d: "Attend/lodge with the Immigration Department and settle penalties." },
      { t: "Resolution", d: "Obtain exit permit or regularise your legal stay." },
    ],
    processing: "1–5 working days depending on overstay length and office",
    fee_note: "Administrative fines are set by Vietnamese authorities based on overstay duration. Our service fee and estimated penalty are provided in the quotation.",
    regulations: [
      "Decree No. 144/2021/ND-CP on administrative penalties",
      "Law No. 47/2014/QH13 on Entry, Exit, Transit and Residence of Foreigners",
    ],
    fields: [
      { name: "overstay_from", label: "Date Permitted Stay Expired", type: "date", placeholder: "DD/MM/YYYY", required: true },
      { name: "last_visa_type", label: "Last Visa / Entry Type", type: "text" },
      { name: "overstay_reason", label: "Reason for Overstay", type: "textarea", required: true },
      { name: "address_vn", label: "Current Address in Vietnam", type: "text", required: true },
      { name: "intended_action", label: "Intended Action", type: "select", options: ["Exit Vietnam", "Regularise / extend stay"] },
    ],
  },
];

export const SERVICE_MAP = Object.fromEntries(SERVICES.map((s) => [s.key, s]));
export const SLUG_MAP = Object.fromEntries(SERVICES.map((s) => [s.slug, s]));

export const FIELD_LABELS = (() => {
  const m = {};
  PASSPORT_FIELDS.forEach((f) => (m[f.name] = f.label));
  SERVICES.forEach((s) => s.fields.forEach((f) => (m[f.name] = f.label)));
  m.email = "Email";
  m.phone = "Phone";
  m.overstay_days = "Days Overstayed";
  m.overstay_fee_usd = "Estimated Overstay Fee (USD)";
  return m;
})();

export const OFFICES = [
  { city: "Hanoi", address: "44-46 Pham Ngoc Thach, Dong Da, Hanoi, Vietnam" },
  { city: "Ho Chi Minh City", address: "333-337 Nguyen Trai Street, District 1, Ho Chi Minh City, Vietnam" },
];
export const CONTACT = {
  phone: "+842438257941",
  phoneDisplay: "+84 24 3825 7941",
  email: "info@immilaw-vn.com",
};

export const STATUS_META = {
  submitted: { label: "Submitted", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  quoted: { label: "Quotation Ready", cls: "bg-amber-50 text-brand-amber border-amber-200" },
  paid: { label: "Paid", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  processing: { label: "Processing", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  completed: { label: "Completed", cls: "bg-emerald-600 text-white border-emerald-700" },
  cancelled: { label: "Cancelled", cls: "bg-red-50 text-brand-red border-red-200" },
};
